import {
  TableCellType,
  TableHeader,
} from "../../../components/custom-table/table";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import {
  TableCellMultiline,
  TableCellThreeDotsButton,
  TableCellWithBadge,
} from "../../../data-model/tableCells";
import { CallingWorklistTableDataModel } from "../../../data-model/tablesData";
import {
  CallingStatus,
  CALLING_STATUS_COLORS,
} from "../../../enums/callingStatus";
import { CmmStpStatus } from "../../../enums/cmmStpStatus";
import {
  SecondStpStatus,
  SecondStpStatusLabels,
} from "../../../enums/secondStpStatus";
import { PharmaStpFileTableColumnKeys } from "../../../enums/tableColumnKeys";
import { getCmmStatuS } from "../../pharmaStpFile/table/utils/getCorrectCmmStatus";
import { getSftpBadgeStyles } from "../../pharmaStpFile/table/utils/getSftpBadgeStyles";
import { buildDowCell } from "./utils/buildDowCell";
import { formatDob } from "./utils/formatDob";
import { getDenialLetterUploadStatus } from "./utils/getCorrectDenialLetterUploadStatus";
import { getStpFileStatus } from "./utils/getCorrectStatus";
import { getTextNotesStatus } from "./utils/getCorrectTextNotesStatus";
import getPoc from "./utils/getPoc";
import {
  getBgColorForStpStatus,
  getTextColorForStpStatus,
} from "./utils/getStpStatusColors";

export const CallingWorklistTableHeader: TableHeader[] = [
  {
    label: "",
    key: PharmaStpFileTableColumnKeys.IDENTIFIER,
    order: 0,
    width: 3,
    sortable: false,
    type: TableCellType.COPY_ICON,
  },
  {
    label: "DOW",
    key: PharmaStpFileTableColumnKeys.DATE_OF_WORK,
    order: 1,
    width: 13,
    sortable: true,
    type: TableCellType.MULTILINE,
  },
  // {
  //   label: "MRN",
  //   key: PharmaStpFileTableColumnKeys.PATIENT_MRN,
  //   order: 3,
  //   width: 8,
  //   sortable: true,
  //   type: TableCellType.STRING,
  // },
  {
    label: "Patient Name",
    key: PharmaStpFileTableColumnKeys.PATIENT_NAME,
    order: 2,
    width: 12,
    sortable: false,
    type: TableCellType.MULTILINE,
  },
  {
    label: "DOB",
    key: PharmaStpFileTableColumnKeys.DOB,
    order: 3,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Drug",
    key: PharmaStpFileTableColumnKeys.DRUG,
    order: 4,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  // {
  //   label: "BIN",
  //   key: PharmaStpFileTableColumnKeys.BIN,
  //   order: 7,
  //   width: 8,
  //   sortable: false,
  //   type: TableCellType.STRING,
  // },
  {
    label: "CMM ID",
    key: PharmaStpFileTableColumnKeys.CMM_ID,
    order: 5,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "2nd STP Status",
    key: PharmaStpFileTableColumnKeys.SECOND_STP_STATUS,
    order: 6,
    width: 9,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Response Status",
    key: PharmaStpFileTableColumnKeys.RESPONSE_STATUS,
    order: 7,
    width: 9,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "Case ID",
    key: PharmaStpFileTableColumnKeys.CALLING_CASE_ID,
    order: 8,
    width: 8,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "POC",
    key: PharmaStpFileTableColumnKeys.POC,
    order: 9,
    width: 7,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "Case Status",
    key: PharmaStpFileTableColumnKeys.CALLING_STATUS,
    order: 10,
    width: 8,
    sortable: false,
    type: TableCellType.BADGE,
  },
  {
    label: "CMM",
    key: PharmaStpFileTableColumnKeys.CMM,
    order: 11,
    width: 7,
    sortable: false,
    type: TableCellType.BADGE_BUTTON,
  },
  {
    label: "Action",
    key: PharmaStpFileTableColumnKeys.ADD,
    order: 12,
    width: 6,
    sortable: false,
    type: TableCellType.ADD_KEY_BUTTON,
  },
  {
    label: "Next Follow Up",
    key: PharmaStpFileTableColumnKeys.NEXT_FOLLOW_UP,
    order: 13,
    width: 9,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: PharmaStpFileTableColumnKeys.CALLING_HISTORY_ICON,
    order: 14,
    width: 4,
    sortable: false,
    type: TableCellType.CALLING_HISTORY_ICON,
  },
];

/**
 * Maps calling-worklist API rows to table cell models, including CMM and SFTP badges
 * aligned with the pharma STP file table.
 *
 * @param data - Raw pharma STP / calling worklist order rows
 * @param orgId - Selected organization id, used to resolve the DOW source
 * @returns Rows formatted for `CustomTable` (Calling worklist)
 */
export const generateTableDataForCallingWorklistTable = (
  data: PharmaStpFileModel[],
  orgId: string = "",
): CallingWorklistTableDataModel[] => {
  return data.map((item) => {
    const dow: TableCellMultiline = buildDowCell(item, orgId);
    const getMrnForTable = (item: PharmaStpFileModel) => {
      return item?.patient_mrn ?? item?.seq ?? "";
    };
    const patient_mrn: string = getMrnForTable(item);
    const patientName: TableCellMultiline = {
      mainText: item?.patient_name ?? "",
      secondaryText: `MRN : ${patient_mrn}`,
    };
    const dob: string = formatDob(item?.dob);
    const drug: string = item?.drug ?? "";

    const cmmIdValue = item?.covermymed_id;
    const cmmId: string =
      !cmmIdValue || cmmIdValue === "nan" || cmmIdValue === "NaN"
        ? ""
        : String(cmmIdValue);

    const poc: string = getPoc(item?.non_epa_poc ?? "");
    const identifier: string = item?.identifier ?? "";

    // const shouldDisableAddButton = Boolean(
    //   item?.covermymed_id &&
    //   item.covermymed_id !== "nan" &&
    //   item.covermymed_id !== "NaN" &&
    //   item.covermymed_id !== "",
    // );

    const addText = item?.covermymed_id ? "Edit" : "Add";

    const add = {
      value: "Add",
      isDisabled: false,
      title: addText,
    };

    const sftpStyles = getSftpBadgeStyles(item?.sftp_status);
    const sftp: TableCellWithBadge = {
      text: sftpStyles.title,
      color: sftpStyles.textColor,
      bgColor: sftpStyles.bgColor,
      hoverText: sftpStyles.title,
    };

    const cmm: TableCellWithBadge = getCmmStatuS(
      item.meta?.cmm_st_rpa_status ?? "",
    );

    const secondStpStatusBadge: TableCellWithBadge = item?.second_stp_status
      ? {
          // Falls back to the raw value so an unmapped status is not a blank badge.
          text:
            SecondStpStatusLabels[item.second_stp_status as SecondStpStatus] ??
            item.second_stp_status,
          color: getTextColorForStpStatus(
            item.second_stp_status as CmmStpStatus,
          ),
          bgColor: getBgColorForStpStatus(
            item.second_stp_status as CmmStpStatus,
          ),
        }
      : {
          text: "Not Sent",
          color: "#111111",
          bgColor: "#F5F5F5",
        };

    const responseStpStatusBadge: TableCellWithBadge = item?.response_status
      ? getStpFileStatus(item.response_status)
      : {
          text: item?.response_status || "--",
          color: "#0F0F0F",
          bgColor: "#F5F5F5",
        };

    const insuranceId: TableCellMultiline = {
      mainText: item?.insuranceid ?? "",
      secondaryText: `BIN : ${item?.bin ?? ""}`,
    };
    const callingCaseId: string = item?.non_epa_case_id ?? "";

    // Orders with no summary saved yet count as Unresolved.
    const callingStatusText =
      item?.non_epa_status?.trim() || CallingStatus.UNRESOLVED;
    const callingStatusColors =
      CALLING_STATUS_COLORS[callingStatusText as CallingStatus] ??
      CALLING_STATUS_COLORS[CallingStatus.UNRESOLVED];
    const callingStatus: TableCellWithBadge = {
      text: callingStatusText,
      color: callingStatusColors.color,
      bgColor: callingStatusColors.backgroundColor,
    };

    const textNote: TableCellWithBadge = getTextNotesStatus(
      item.text_note_status ?? "",
    );
    const letterUpload: TableCellWithBadge = getDenialLetterUploadStatus(
      item.meta?.denial_letter_upload_status ?? "",
    );

    const binValue = item?.bin;
    const bin: string =
      !binValue || binValue === "nan" || binValue === "NaN"
        ? ""
        : String(binValue);

    const action: TableCellThreeDotsButton = {
      threeDotsOptions: [{ id: "rerun_status", text: "Rerun Status" }],
      rowId: item.identifier,
    };

    const callReason: string = item?.non_epa_reason ?? "";
    const nextFollowUp: string = formatDob(
      item?.non_epa_follow_up_date ?? undefined,
    );

    return {
      dow,
      nextFollowUp,
      patient_mrn,
      patientName,
      dob,
      drug,
      letterUpload,
      callingCaseId,
      callingStatus,
      textNote,
      bin,
      insuranceId,
      cmmId,
      secondStpStatus: secondStpStatusBadge,
      responseStpStatus: responseStpStatusBadge,
      add,
      poc,
      cmm,
      sftp,
      identifier,
      callReason,
      textNoteStatus: item.text_note_status,
      letterUploadStatus: item.meta?.denial_letter_upload_status,
      letterUploadUpdatedAt:
        item.meta?.denial_letter_upload_status_updated_at ?? "",
      cmmStatus: item.meta?.cmm_st_rpa_status,
      action,
    };
  });
};
